import { Sides } from "@project/emerge-types";
import find from "lodash/find";
import MakerJS from "makerjs";
import { Vector3 } from "three";
import { EdgeIndex } from "../../enum/EdgeIndex";
import { EdgeType } from "../../enum/EdgeType";
import { InteriorType } from "../../enum/InteriorType";
import { LTCaseUserOption } from "../../enum/LTCaseUserOption";
import { LTProducts } from "../../enum/LTProducts";
import type { CaseInteriorOptions } from "../../interfaces/AccessoryCompartmentOptions";
import type {
  ExtendedCutoutDefinition,
  SerializedCutout,
} from "../../interfaces/CutoutProperties";
import type { FlightCaseBlueprint } from "../../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import { DefaultGroupName } from "../../structure/Rect";
import { RectPropertiesBySide } from "../../structure/RectProperties";
import { OptionDBInstance } from "../../user_options/options_db";
import {
  CLEANCUT,
  calculateProfileOffset,
  getVector3Index,
  MILLING_EXTRA,
} from "./InteriorHelpers";
import type { PadEdgeCutoutPoint } from "./PaddingHelpers";

interface EdgeProfileContext {
  isHood: boolean;
  upperPaddingAlignment: boolean;
  cut0: { offset?: number; groupName?: string } | undefined;
  profileOffset: number;
  coverForGroup: { borderPaddingType?: { strength?: number } } | undefined;
  deltaW: number;
  height: number;
  extra: number;
  strength: number;
}

/**
 * Shared exterior-profile/edge context used by both tray-panel and interior-pad
 * cutout generation. Keeps the profile geometry math in one place.
 */
function buildEdgeProfileContext(
  settings: FlightCaseSettings,
): EdgeProfileContext {
  const isHood =
    settings.type === LTProducts.Haube ||
    settings.type === LTProducts.KeyboardCase;
  const profiles = settings.exteriorOptions?.profiles;
  const gapWidth = profiles?.gapWidth ?? 0;
  const extra = profiles?.extra ?? 0;
  const height = profiles?.height ?? 0;
  const strength = profiles?.strength ?? 0;
  const deltaW =
    gapWidth - (settings.exteriorOptions?.strength ?? 0) + strength;
  const upperPaddingAlignment =
    settings.interiorOptions?.upperPaddingAlignment ?? false;
  const profileOffset = calculateProfileOffset(
    profiles,
    isHood ? true : upperPaddingAlignment,
  );
  const cut0 = settings.cuts?.[0];
  const groupName = cut0?.groupName ?? DefaultGroupName;
  const coverForGroup = settings.coverPaddings?.[groupName];

  return {
    isHood,
    upperPaddingAlignment,
    cut0,
    profileOffset,
    coverForGroup,
    deltaW,
    height,
    extra,
    strength,
  };
}

export function createRectangularCutout(
  width: number,
  height: number,
  side: Sides,
  position: Vector3,
  orientation = 0,
): ExtendedCutoutDefinition {
  const shape = MakerJS.model.center(
    new MakerJS.models.Rectangle(width, height),
  );
  const pathData = MakerJS.exporter.toSVGPathData(shape, {
    origin: [0, 0],
  });
  const path = typeof pathData === "string" ? pathData : "";
  return {
    cut: 0,
    path,
    side,
    position: position.clone(),
    orientation,
  };
}

export function createHoodCutouts(options: {
  position: Vector3;
  side: Sides;
  panelHeight: number;
  cut0: { offset?: number } | undefined;
  profileOffset: number;
  coverForGroup: { borderPaddingType?: { strength?: number } } | undefined;
  deltaW: number;
  height: number;
  extra: number;
  strength: number;
}): ExtendedCutoutDefinition[] {
  const cutouts: ExtendedCutoutDefinition[] = [];

  // Padding Cutout
  const paddingWidth =
    2 * (options.coverForGroup?.borderPaddingType?.strength ?? 0);
  const paddingHeight =
    2 *
    (options.panelHeight - (options.cut0?.offset ?? 0) - options.profileOffset);

  cutouts.push(
    createRectangularCutout(
      paddingWidth,
      paddingHeight,
      options.side,
      options.position,
    ),
  );

  // Profile Cutout
  const profileWidth = 2 * options.deltaW;
  const profileHeight =
    2 *
    (options.panelHeight -
      (options.cut0?.offset ?? 0) -
      options.profileOffset +
      options.height +
      options.extra -
      options.strength +
      MILLING_EXTRA);

  cutouts.push(
    createRectangularCutout(
      profileWidth,
      profileHeight,
      options.side,
      options.position,
    ),
  );

  return cutouts;
}

export function createUpperPaddingAlignmentCutouts(options: {
  position: Vector3;
  side: Sides;
  deltaW: number;
  height: number;
  extra: number;
  strength: number;
}): ExtendedCutoutDefinition[] {
  const profileWidth = 2 * options.deltaW;
  const profileHeight =
    2 * (options.height + options.extra - options.strength + MILLING_EXTRA);

  return [
    createRectangularCutout(
      profileWidth,
      profileHeight,
      options.side,
      options.position,
    ),
  ];
}

export function createCaseInteriorTProfileCutouts(
  position: Vector3,
  side: Sides,
  panelHeight: number,
  caseInterior: CaseInteriorOptions,
  crossSections: Vector3[],
): ExtendedCutoutDefinition[] {
  if (!caseInterior.trayMaterial?.tProfileCode || crossSections.length > 0) {
    return [];
  }

  const tProfileWidth = 1.5;
  const tProfileHeight = panelHeight + CLEANCUT;

  return [
    createRectangularCutout(tProfileWidth, tProfileHeight, side, position),
  ];
}

export function createCrossSectionCutouts(
  settings: FlightCaseSettings,
  side: Sides,
  panelHeight: number,
  crossSections: Vector3[],
): ExtendedCutoutDefinition[] {
  if (settings.interiorOptions.type !== InteriorType.CASE) {
    return [];
  }

  const caseInterior = settings.interiorOptions;
  let effectiveTrayMaterial = caseInterior.trayMaterial;
  if (!effectiveTrayMaterial) {
    console.warn(
      "[InteriorBuildStep] Missing trayMaterial for cross sections. Using default.",
    );
    effectiveTrayMaterial = OptionDBInstance.getByName(
      LTCaseUserOption.TrayMaterialType,
      "PLW1415",
    );
    if (!effectiveTrayMaterial) {
      console.warn(
        "[InteriorBuildStep] Could not load default trayMaterial. Skipping cross sections.",
      );
      return [];
    }
  }

  if (crossSections.length === 0) {
    return [];
  }

  const cutouts: ExtendedCutoutDefinition[] = [];
  for (const crossSection of crossSections) {
    const crossSectionWidth = effectiveTrayMaterial.strength;
    const crossSectionHeight = panelHeight + 6;
    const crossSectionPosition = new Vector3(
      crossSection.x,
      side === Sides.FRONT ? panelHeight / 2 : -panelHeight / 2,
      crossSection.z,
    );

    cutouts.push(
      createRectangularCutout(
        crossSectionWidth,
        crossSectionHeight,
        side,
        crossSectionPosition,
      ),
    );
  }

  return cutouts;
}

export function createEdgePointCutouts(options: {
  point: Vector3;
  horizontal: "x" | "y" | "z";
  blueprint: FlightCaseBlueprint;
  panelHeight: number;
  settings: FlightCaseSettings;
  side: Sides;
  isHood: boolean;
  upperPaddingAlignment: boolean;
  cut0: { offset?: number } | undefined;
  profileOffset: number;
  coverForGroup: { borderPaddingType?: { strength?: number } } | undefined;
  deltaW: number;
  height: number;
  extra: number;
  strength: number;
  crossSections: Vector3[];
}): ExtendedCutoutDefinition[] {
  const pointComponent = options.point.getComponent(
    getVector3Index(options.horizontal),
  );
  const blueprintComponent =
    options.blueprint.innerDimensions.getComponent(
      getVector3Index(options.horizontal),
    ) / 2;
  const isEdgePoint = Math.abs(pointComponent) === blueprintComponent;

  if (!isEdgePoint) {
    return [];
  }

  const position = options.point.clone();
  position.y = options.panelHeight - options.blueprint.innerDimensions.y / 2;

  const cutouts: ExtendedCutoutDefinition[] = [];

  if (options.isHood) {
    cutouts.push(
      ...createHoodCutouts({
        position,
        side: options.side,
        panelHeight: options.panelHeight,
        cut0: options.cut0,
        profileOffset: options.profileOffset,
        coverForGroup: options.coverForGroup,
        deltaW: options.deltaW,
        height: options.height,
        extra: options.extra,
        strength: options.strength,
      }),
    );
  } else if (options.upperPaddingAlignment) {
    cutouts.push(
      ...createUpperPaddingAlignmentCutouts({
        position,
        side: options.side,
        deltaW: options.deltaW,
        height: options.height,
        extra: options.extra,
        strength: options.strength,
      }),
    );
  }

  if (options.settings.interiorOptions.type === InteriorType.CASE) {
    const caseInterior = options.settings.interiorOptions;
    cutouts.push(
      ...createCaseInteriorTProfileCutouts(
        position,
        options.side,
        options.panelHeight,
        caseInterior,
        options.crossSections,
      ),
    );
  }

  return cutouts;
}

export function createTrayPanelCutouts(
  settings: FlightCaseSettings,
  blueprint: FlightCaseBlueprint,
  panelHeight: number,
  center: Vector3,
  width: number,
  side: Sides,
  crossSections: Vector3[],
): ExtendedCutoutDefinition[] {
  const points: Vector3[] =
    side === Sides.LEFT || side === Sides.RIGHT
      ? [
          center.clone().add(new Vector3(0, 0, width / 2)),
          center.clone().add(new Vector3(0, 0, -width / 2)),
        ]
      : [
          center.clone().add(new Vector3(width / 2, 0, 0)),
          center.clone().add(new Vector3(-width / 2, 0, 0)),
        ];

  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) return [];
  const { horizontal } = rectProps;
  const cutouts: ExtendedCutoutDefinition[] = [];
  const edgeContext = buildEdgeProfileContext(settings);

  for (const point of points) {
    cutouts.push(
      ...createEdgePointCutouts({
        point,
        horizontal,
        blueprint,
        panelHeight,
        settings,
        side,
        ...edgeContext,
        crossSections,
      }),
    );
  }

  cutouts.push(
    ...createCrossSectionCutouts(settings, side, panelHeight, crossSections),
  );

  return cutouts;
}

function deriveCutHeightFromCutouts(partSelection: {
  cutouts?: unknown[];
}): number | null {
  const cutouts = partSelection.cutouts;
  if (!Array.isArray(cutouts) || cutouts.length === 0) return null;

  const firstCutout = cutouts[0] as { path?: string } | undefined;
  if (!firstCutout?.path) return null;

  try {
    const model = MakerJS.importer.fromSVGPathData(firstCutout.path);
    const measure = MakerJS.measure.modelExtents(model);
    return measure?.height ?? null;
  } catch {
    return null;
  }
}

function computeConnectorCutout(
  connector: { position: Vector3; partSelection: unknown },
  edgeCenterX: number,
  panelPositionY: number,
  trayStrength: number,
  horizontalSide: Sides,
): ExtendedCutoutDefinition | null {
  const dist = Math.abs(connector.position.y - panelPositionY);
  let cut = (connector.partSelection as { cut?: number })?.cut ?? 0;
  let connectorHeight = 2 * 50;

  if (cut === 0 && "cutouts" in (connector.partSelection as object)) {
    const derived = deriveCutHeightFromCutouts(
      connector.partSelection as { cutouts?: unknown[] },
    );
    if (derived === null) return null; // Skip this connector (no valid path)
    cut = derived;
  }

  if (cut > 0) {
    connectorHeight = Math.max(connectorHeight, cut + 20);
  }

  if (cut / 2 + trayStrength / 2 <= dist) return null;

  const connectorWidth = 2 * (9 + 6.5);
  const connectorPosition = new Vector3(
    edgeCenterX,
    panelPositionY,
    connector.position.z,
  );

  return createRectangularCutout(
    connectorWidth,
    connectorHeight,
    horizontalSide,
    connectorPosition,
  );
}

function processHorizontalEdge(
  edge: {
    type: EdgeType;
    center: Vector3;
    connectors: { position: Vector3; partSelection: unknown }[];
  },
  horizontalSide: Sides,
  panelPositionY: number,
  cutoutWidth: number,
  cutoutHeight: number,
  trayStrength: number,
): ExtendedCutoutDefinition[] {
  if (edge.type !== EdgeType.FROMCUT) return [];

  const cutouts: ExtendedCutoutDefinition[] = [];
  const cutoutPosition = new Vector3(
    edge.center.x,
    panelPositionY,
    edge.center.z,
  );

  cutouts.push(
    createRectangularCutout(
      cutoutWidth,
      cutoutHeight,
      horizontalSide,
      cutoutPosition,
    ),
  );

  for (const connector of edge.connectors) {
    const connectorCutout = computeConnectorCutout(
      connector,
      edge.center.x,
      panelPositionY,
      trayStrength,
      horizontalSide,
    );
    if (connectorCutout) cutouts.push(connectorCutout);
  }

  return cutouts;
}

export function collectRackHorizontalCutouts(
  blueprint: FlightCaseBlueprint,
  panelPosition: Vector3,
  deltaW: number,
  profileHeight: number,
  extra: number,
  profileStrength: number,
  trayStrength: number,
): ExtendedCutoutDefinition[] {
  const cutouts: ExtendedCutoutDefinition[] = [];
  const cutoutWidth = 2 * deltaW;
  const cutoutHeight =
    2 * (profileHeight + extra - profileStrength + MILLING_EXTRA);

  for (const horizontalSide of [Sides.LEFT, Sides.RIGHT]) {
    const profileRect = find(blueprint.rects, {
      side: horizontalSide,
      group: DefaultGroupName,
    });
    if (!profileRect) continue;

    for (const edgeIndex of [EdgeIndex.LEFT, EdgeIndex.RIGHT]) {
      const edge = profileRect.edges[edgeIndex];
      if (!edge) continue;

      const edgeCutouts = processHorizontalEdge(
        edge,
        horizontalSide,
        panelPosition.y,
        cutoutWidth,
        cutoutHeight,
        trayStrength,
      );
      cutouts.push(...edgeCutouts);
    }
  }

  return cutouts;
}

export function collectRackVerticalCutouts(
  blueprint: FlightCaseBlueprint,
  rowIndex: number,
  rowsCount: number,
  panelPosition: Vector3,
  profile: {
    deltaW: number;
    profileHeight: number;
    extra: number;
    profileStrength: number;
  },
): ExtendedCutoutDefinition[] {
  const cutouts: ExtendedCutoutDefinition[] = [];
  if (rowIndex !== 0 && rowIndex !== rowsCount - 1) return cutouts;
  const includeFloorRect = rowIndex === rowsCount - 1;
  const includeLidRect = rowIndex === 0;
  for (const verticalSide of [Sides.BOTTOM, Sides.TOP]) {
    if (verticalSide === Sides.BOTTOM && !includeFloorRect) continue;
    if (verticalSide === Sides.TOP && !includeLidRect) continue;
    const profileRect = find(blueprint.rects, {
      side: verticalSide,
      group: DefaultGroupName,
    });
    if (!profileRect) continue;
    for (const edgeIndex of [EdgeIndex.TOP, EdgeIndex.BOTTOM]) {
      const edge = profileRect.edges[edgeIndex];
      if (edge?.type !== EdgeType.FROMCUT) continue;
      const cutoutWidth = 2 * profile.deltaW;
      const cutoutHeight =
        2 *
        (profile.profileHeight +
          profile.extra -
          profile.profileStrength +
          MILLING_EXTRA);
      const cutoutPosition = new Vector3(
        panelPosition.x,
        edge.center.y,
        edge.center.z,
      );

      cutouts.push(
        createRectangularCutout(
          cutoutWidth,
          cutoutHeight,
          verticalSide,
          cutoutPosition,
        ),
      );
    }
  }
  return cutouts;
}

export function serializeExtendedCutouts(
  cutouts: ExtendedCutoutDefinition[],
  options?: { interiorEdge?: boolean },
): SerializedCutout[] {
  return cutouts.map((c) => ({
    cut: c.cut ?? 0,
    path: c.path,
    side: c.side,
    position:
      c.position instanceof Vector3
        ? c.position.clone()
        : new Vector3(c.position.x, c.position.y, c.position.z),
    orientation: c.orientation ?? 0,
    ...(options?.interiorEdge ? { interiorEdge: true } : {}),
  }));
}

/**
 * Edge/profile cutouts for compartment and accessory-compartment foam (same rules as
 * tray panels). `edgeCutoutPoints` are the pre-shrink case-boundary points collected in
 * {@link createPaddedCube}; shrinking pads off the case wall must not drop their notches.
 */
export function createInteriorPadCutouts(
  settings: FlightCaseSettings,
  blueprint: FlightCaseBlueprint,
  panelHeight: number,
  padSide: Sides,
  edgeCutoutPoints: PadEdgeCutoutPoint[],
): ExtendedCutoutDefinition[] {
  if (padSide === Sides.TOP || edgeCutoutPoints.length === 0) {
    return [];
  }

  const edgeContext = {
    ...buildEdgeProfileContext(settings),
    crossSections: [] as Vector3[],
  };

  const cutouts: ExtendedCutoutDefinition[] = [];
  for (const { point, horizontal } of edgeCutoutPoints) {
    cutouts.push(
      ...createEdgePointCutouts({
        point,
        horizontal,
        blueprint,
        panelHeight,
        settings,
        side: padSide,
        ...edgeContext,
      }),
    );
  }

  return cutouts;
}
